import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  Typography,
} from "@mui/material";
import { isDarkMode } from "../../utils/constants";
import { getDenomination, isForexAssetType } from "./Charting/utils";
import {
  getUserTradeProposals,
  updateTradeProposalTracking,
  updateUserWatchlist,
} from "../../api/user";
import { useAppContext } from "../../contexts/AppContext";
import { useChatContext } from "../../contexts/ChatContext";
import { useNavigate } from "react-router-dom";
import UpgradeMaxModal from "./UpgradeMaxModal";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  buildMaxUpgradeModalProps,
  maxUpgradePathFor,
} from "../../utils/max_upgrade_trial";

const DEFAULT_PRICE_FORMATTER = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 4,
});
const FOREX_PRICE_FORMATTER = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 5,
  maximumFractionDigits: 5,
});
const NON_MAX_ACTIVE_TRACKED_TRADE_LIMIT = 2;

const toNonEmptyString = (value) => {
  if (value == null) return null;
  const str = String(value).trim();
  return str.length ? str : null;
};

const formatTradePrice = (value, denomination = "", assetType = null) => {
  if (value == null || value === "") return null;
  const num = typeof value === "number" ? value : Number(value);
  if (Number.isFinite(num)) {
    const formatter = isForexAssetType(assetType)
      ? FOREX_PRICE_FORMATTER
      : DEFAULT_PRICE_FORMATTER;
    return `${denomination}${formatter.format(num)}`;
  }
  return toNonEmptyString(value);
};

const toTradeNumber = (value) => {
  if (value == null || value === "") return null;
  const num = typeof value === "number" ? value : Number(String(value).replace(/,/g, "").trim());
  return Number.isFinite(num) ? num : null;
};

const formatPercentFromEntry = (entryPrice, levelPrice) => {
  const entry = toTradeNumber(entryPrice);
  const level = toTradeNumber(levelPrice);
  if (entry == null || level == null || entry === 0) return null;
  const pct = ((level - entry) / entry) * 100;
  if (!Number.isFinite(pct)) return null;
  const rounded = Math.round(pct * 100) / 100;
  const sign = rounded > 0 ? "+" : "";
  return `${sign}${rounded.toFixed(2)}%`;
};

const formatTradeDuration = (value) => {
  const str = toNonEmptyString(value);
  if (!str) return null;
  const normalized = str.replace(/[_-]+/g, " ");
  return normalized.replace(/\b\w/g, (c) => c.toUpperCase());
};

const getWatchlistItems = (watchlistData) =>
  Object.values(watchlistData || {}).flatMap((items) =>
    Array.isArray(items) ? items : [],
  );

export const ActionBox = ({
  trade,
  tickerLabel,
  assetType,
  isMobile,
  chatMessageId,
}) => {
  if (!trade || typeof trade !== "object") return null;

  const appContext = useAppContext();
  const userData = appContext?.appProps?.userData;
  const watchlistData = appContext?.appProps?.watchlistData;
  const setWatchlistData = appContext?.appProps?.setWatchlistData;
  const setIsRefreshing = appContext?.appProps?.setIsRefreshing;
  const setPanel = appContext?.appProps?.setPanel;
  const chatContext = useChatContext();
  const setIsSidebarOpen = chatContext?.setIsSidebarOpen;
  const navigate = useNavigate();
  const canManageTradeActions = Boolean(userData?.user_id);

  const [isTracked, setIsTracked] = useState(trade?.tracked === true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isWatchlisted, setIsWatchlisted] = useState(false);
  const [isWatchlistUpdating, setIsWatchlistUpdating] = useState(false);
  const [showTrackLimitModal, setShowTrackLimitModal] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const isMaxTierSubscriber =
    Boolean(userData?.has_active_subscription) &&
    userData?.subscription_plan_tier === "max";
  const trackLimitUpgradeProps = buildMaxUpgradeModalProps(userData, {
    title: "Unlock more trade tracking.",
    description: `Free and paid plans can track up to ${NON_MAX_ACTIVE_TRACKED_TRADE_LIMIT} current trades at the same time. Upgrade to Max for unlimited autonomous simultaneous trade tracking.`,
    bullets: [],
    footer: "Upgrade when you're ready to track more current trades.",
    trialTitle: "Try MAX free for 7 days.",
    trialDescription: `Start your 7-day MAX trial to track more than ${NON_MAX_ACTIVE_TRACKED_TRADE_LIMIT} current trades at the same time.`,
    trialFooter:
      "Today is $0. Your Pro subscription stays active during the trial.",
  });
  const tradeChatMessageId =
    trade?.chat_message_id ?? trade?.chatMessageId ?? chatMessageId;
  const tradeTickerIdRaw =
    trade?.ticker_id ??
    trade?.tickerId ??
    trade?.all_data_ticker_id ??
    trade?.allDataTickerId;
  const parsedTradeTickerId = Number(tradeTickerIdRaw);
  const tradeTickerId = Number.isFinite(parsedTradeTickerId)
    ? parsedTradeTickerId
    : null;

  useEffect(() => {
    if (trade?.tracked == null) {
      setIsTracked(false);
      return;
    }
    setIsTracked(trade.tracked === true);
  }, [trade?.tracked]);

  useEffect(() => {
    if (tradeTickerId == null) {
      setIsWatchlisted(false);
      return;
    }
    const listed = getWatchlistItems(watchlistData).some(
      (item) => Number(item?.id) === tradeTickerId,
    );
    setIsWatchlisted(listed);
  }, [watchlistData, tradeTickerId]);

  const denomination = getDenomination(assetType);
  const entryPriceRaw = trade.entry_price ?? trade.entryPrice;
  const stopLossRaw = trade.stop_loss ?? trade.stopLoss;
  const priceTargetRaw = trade.price_target ?? trade.priceTarget;
  const entryPrice = formatTradePrice(
    entryPriceRaw,
    denomination,
    assetType,
  );
  const stopLoss = formatTradePrice(
    stopLossRaw,
    denomination,
    assetType,
  );
  const priceTarget = formatTradePrice(
    priceTargetRaw,
    denomination,
    assetType,
  );
  const stopLossPctFromEntry = formatPercentFromEntry(entryPriceRaw, stopLossRaw);
  const targetPctFromEntry = formatPercentFromEntry(entryPriceRaw, priceTargetRaw);
  const rewardRiskFromTrade = toTradeNumber(
    trade.reward_risk_ratio ?? trade.rewardRiskRatio,
  );
  const computedRewardRisk = (() => {
    const entry = toTradeNumber(entryPriceRaw);
    const stop = toTradeNumber(stopLossRaw);
    const target = toTradeNumber(priceTargetRaw);
    if (entry == null || stop == null || target == null) return null;

    let risk = null;
    let reward = null;
    if (stop < entry) {
      // Long setup
      risk = entry - stop;
      reward = target - entry;
    } else if (stop > entry) {
      // Short setup
      risk = stop - entry;
      reward = entry - target;
    } else {
      return null;
    }

    if (risk <= 0 || reward <= 0) return null;
    const ratio = reward / risk;
    return Number.isFinite(ratio) ? ratio : null;
  })();
  const rewardRiskRatio = rewardRiskFromTrade ?? computedRewardRisk;
  const rewardRiskLabel =
    rewardRiskRatio != null ? rewardRiskRatio.toFixed(2) : null;

  const expectedDuration = formatTradeDuration(
    trade.expected_trade_duration ?? trade.expectedTradeDuration,
  );
  const notes = toNonEmptyString(trade.notes ?? trade.note ?? trade.details);

  const hasAny =
    entryPrice ||
    stopLoss ||
    priceTarget ||
    expectedDuration ||
    rewardRiskLabel ||
    notes ||
    tickerLabel;
  if (!hasAny) return null;

  const colors = isDarkMode
    ? {
        bg: "linear-gradient(180deg, rgba(17,19,24,0.9) 0%, rgba(11,12,16,0.95) 100%)",
        border: "rgba(255,255,255,0.08)",
        text: "#f9fafb",
        muted: "#9ca3af",
        kicker: "#a5b4fc",
        chipBg: "rgba(59,130,246,0.18)",
        chipText: "#bfdbfe",
        chipBorder: "rgba(59,130,246,0.35)",
      }
    : {
        bg: "linear-gradient(180deg, #ffffff 0%, #f1f5f9 100%)",
        border: "#e5e7eb",
        text: "#0f172a",
        muted: "#6b7280",
        kicker: "#1d4ed8",
        chipBg: "rgba(59,130,246,0.12)",
        chipText: "#1d4ed8",
        chipBorder: "rgba(59,130,246,0.25)",
      };

  const valueOrDash = (val) => (val != null && val !== "" ? val : "—");
  const renderPriceWithPercent = (priceText, pctText) => {
    if (!priceText) return null;
    if (!pctText) return priceText;
    return (
      <>
        <span>{priceText}</span>
        <span
          style={{
            marginLeft: "4px",
            fontSize: isMobile ? "0.74rem" : "0.78rem",
            color: colors.muted,
            opacity: isDarkMode ? 0.74 : 0.78,
            fontWeight: 500,
          }}
        >
          ({pctText})
        </span>
      </>
    );
  };

  const handleTrackingChange = async (event) => {
    const nextTracked = event?.target?.checked ?? !isTracked;
    if (isUpdating || nextTracked === isTracked || !tradeChatMessageId) return;

    if (nextTracked && !isMaxTierSubscriber) {
      setIsUpdating(true);
      const proposals = await getUserTradeProposals();
      setIsUpdating(false);
      if (Array.isArray(proposals)) {
        const currentTrackedCount = proposals.filter(
          (proposal) =>
            String(proposal?.status || "").toLowerCase() !== "closed",
        ).length;
        if (currentTrackedCount >= NON_MAX_ACTIVE_TRACKED_TRADE_LIMIT) {
          setShowTrackLimitModal(true);
          return;
        }
      }
    }

    setIsTracked(nextTracked);

    setIsUpdating(true);
    const resp = await updateTradeProposalTracking(tradeChatMessageId, nextTracked);
    setIsUpdating(false);
    if (resp?.error) {
      setIsTracked(!nextTracked);
      return;
    }
    window.dispatchEvent(new CustomEvent("trade-proposals:updated"));
    if (nextTracked) {
      if (typeof setIsSidebarOpen === "function") {
        setIsSidebarOpen(true);
      }
      if (typeof setPanel === "function") {
        setPanel("trade_proposals");
      }
      if (typeof window !== "undefined") {
        window.__warpTradeProposalHighlight = {
          chatMessageId: tradeChatMessageId,
          createdAt: Date.now(),
        };
        window.dispatchEvent(
          new CustomEvent("trade-proposals:highlight", {
            detail: { chatMessageId: tradeChatMessageId },
          }),
        );
      }
    }
  };

  const handleWatchlistChange = async (event) => {
    const nextWatchlisted = event?.target?.checked ?? !isWatchlisted;
    if (tradeTickerId == null) return;
    if (typeof setWatchlistData !== "function") return;
    if (isWatchlistUpdating || nextWatchlisted === isWatchlisted) return;

    setIsWatchlisted(nextWatchlisted);
    setIsWatchlistUpdating(true);

    const response = await updateUserWatchlist(
      nextWatchlisted ? [tradeTickerId] : null,
      nextWatchlisted ? null : tradeTickerId,
      setWatchlistData,
      typeof setIsRefreshing === "function" ? setIsRefreshing : () => {},
    );

    setIsWatchlistUpdating(false);
    if (response?.error) {
      setIsWatchlisted(!nextWatchlisted);
      return;
    }

    if (nextWatchlisted) {
      if (typeof setIsSidebarOpen === "function") {
        setIsSidebarOpen(true);
      }
      if (typeof setPanel === "function") {
        setPanel("watchlist");
      }
      if (typeof window !== "undefined") {
        window.__warpWatchlistHighlight = {
          tickerId: tradeTickerId,
          createdAt: Date.now(),
        };
        window.dispatchEvent(
          new CustomEvent("watchlist:highlight", {
            detail: { tickerId: tradeTickerId },
          }),
        );
      }
    }
  };

  return (
    <>
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          boxSizing: "border-box",
          position: "relative",
          borderRadius: "14px",
          border: `1px solid ${colors.border}`,
          background: colors.bg,
          boxShadow: isDarkMode
            ? "0 10px 24px rgba(0,0,0,0.35)"
            : "0 10px 24px rgba(15,23,42,0.08)",
          padding: isMobile ? "12px 12px" : "16px 18px",
          fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        }}
      >
        <IconButton
          aria-label="Trade action info"
          onClick={() => setShowInfoModal(true)}
          size="small"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: colors.muted,
            border: `1px solid ${colors.border}`,
            backgroundColor: isDarkMode
              ? "rgba(255,255,255,0.04)"
              : "rgba(15,23,42,0.02)",
            "&:hover": {
              backgroundColor: isDarkMode
                ? "rgba(255,255,255,0.08)"
                : "rgba(15,23,42,0.08)",
            },
          }}
        >
          <InfoOutlinedIcon fontSize="small" />
        </IconButton>

        {canManageTradeActions && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1,
              mb: 1.5,
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={isTracked}
                  onChange={handleTrackingChange}
                  disabled={isUpdating}
                  sx={{
                    color: colors.kicker,
                    "&.Mui-checked": { color: colors.kicker },
                    "&.Mui-disabled": { color: colors.muted },
                  }}
                />
              }
              label="Track Trade"
              sx={{
                margin: 0,
                color: colors.text,
                opacity: isUpdating ? 0.7 : 1,
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.85rem",
                  fontWeight: 600,
                },
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isWatchlisted}
                  onChange={handleWatchlistChange}
                  disabled={isWatchlistUpdating || tradeTickerId == null}
                  sx={{
                    color: colors.kicker,
                    "&.Mui-checked": { color: colors.kicker },
                    "&.Mui-disabled": { color: colors.muted },
                  }}
                />
              }
              label="Add to Watchlist"
              sx={{
                margin: 0,
                color: colors.text,
                opacity: isWatchlistUpdating ? 0.7 : 1,
                "& .MuiFormControlLabel-label": {
                  fontSize: "0.85rem",
                  fontWeight: 600,
                },
              }}
            />
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Box
              sx={{
                fontSize: "0.72rem",
                fontWeight: 700,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: colors.kicker,
              }}
            >
              Trade Action
            </Box>
          </Box>
          {tickerLabel && (
            <Box
              sx={{
                px: 1.2,
                py: 0.4,
                borderRadius: 999,
                border: `1px solid ${colors.chipBorder}`,
                backgroundColor: colors.chipBg,
                color: colors.chipText,
                fontSize: "0.78rem",
                fontWeight: 600,
                letterSpacing: "0.02em",
                whiteSpace: "nowrap",
                alignSelf: "flex-start",
              }}
            >
              {tickerLabel}
            </Box>
          )}
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "repeat(2, minmax(0, 1fr))"
              : "repeat(3, minmax(0, 1fr))",
            gap: 1.25,
            mt: 1.4,
          }}
        >
          {[
            { label: "Entry", value: valueOrDash(entryPrice) },
            {
              label: "Stop Loss",
              value: valueOrDash(
                renderPriceWithPercent(stopLoss, stopLossPctFromEntry),
              ),
            },
            {
              label: "Target Price",
              value: valueOrDash(
                renderPriceWithPercent(priceTarget, targetPctFromEntry),
              ),
            },
          ].map((item) => (
            <Box key={item.label}>
              <Box
                sx={{
                  fontSize: "0.68rem",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: colors.muted,
                  mb: 0.5,
                }}
              >
                {item.label}
              </Box>
              <Box
                sx={{
                  fontSize: "0.98rem",
                  fontWeight: 600,
                  color: colors.text,
                }}
              >
                {item.value}
              </Box>
            </Box>
          ))}
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "repeat(2, minmax(0, 1fr))"
              : "repeat(3, minmax(0, 1fr))",
            gap: 1.25,
            mt: 1.15,
          }}
        >
          {[
            { label: "Duration", value: valueOrDash(expectedDuration) },
            { label: "Reward/Risk", value: valueOrDash(rewardRiskLabel) },
          ].map((item) => (
            <Box key={item.label}>
              <Box
                sx={{
                  fontSize: "0.68rem",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: colors.muted,
                  mb: 0.5,
                }}
              >
                {item.label}
              </Box>
              <Box
                sx={{
                  fontSize: "0.98rem",
                  fontWeight: 600,
                  color: colors.text,
                }}
              >
                {item.value}
              </Box>
            </Box>
          ))}
        </Box>

        {notes && (
          <Box sx={{ mt: 1.6 }}>
            <Box
              sx={{
                fontSize: "0.68rem",
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: colors.muted,
                mb: 0.6,
              }}
            >
              Notes
            </Box>
            <Box
              sx={{
                fontSize: "0.88rem",
                color: colors.text,
                lineHeight: 1.5,
                whiteSpace: "pre-wrap",
              }}
            >
              {notes}
            </Box>
          </Box>
        )}
      </Box>

      <Dialog
        open={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: "hidden",
            border: `1px solid ${isDarkMode ? "rgba(62,84,118,0.55)" : "rgba(163,189,233,0.55)"}`,
            background: isDarkMode
              ? "linear-gradient(180deg, #131a28 0%, #0d111b 100%)"
              : "linear-gradient(180deg, #ffffff 0%, #f4f8ff 100%)",
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              p: { xs: 2.25, sm: 3 },
              pr: { xs: 5, sm: 6 },
              position: "relative",
              borderBottom: `1px solid ${isDarkMode ? "rgba(68,85,112,0.45)" : "rgba(187,208,243,0.65)"}`,
              background: isDarkMode
                ? "radial-gradient(120% 120% at 100% 0%, rgba(39,129,255,0.3) 0%, rgba(17,27,43,0) 62%)"
                : "radial-gradient(130% 130% at 100% 0%, rgba(47,127,255,0.2) 0%, rgba(255,255,255,0) 62%)",
            }}
          >
            <IconButton
              onClick={() => setShowInfoModal(false)}
              aria-label="Close trade action info dialog"
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                color: isDarkMode ? "#D5E4FF" : "#23529A",
              }}
            >
              <CloseRoundedIcon />
            </IconButton>

            <Typography
              sx={{
                fontSize: "11px",
                textTransform: "uppercase",
                letterSpacing: 1.2,
                fontWeight: 700,
                color: isDarkMode ? "#94B8F6" : "#2D66B7",
              }}
            >
              Trade Actions
            </Typography>
            <Typography
              id="trade-action-info-title"
              sx={{
                mt: 0.6,
                fontSize: { xs: "24px", sm: "30px" },
                lineHeight: 1.08,
                fontWeight: 700,
                color: isDarkMode ? "#F3F8FF" : "#0F294F",
                fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
              }}
            >
              How This Works
            </Typography>
            <Typography
              sx={{
                mt: 1.1,
                fontSize: "14px",
                lineHeight: 1.6,
                color: isDarkMode ? "#A5BEDF" : "#335B8F",
                maxWidth: 520,
              }}
            >
              This panel captures the proposed entry, stop loss, target, and risk profile for the setup.
            </Typography>
          </Box>

          <Box sx={{ p: { xs: 2.25, sm: 3 }, display: "grid", gap: 1.2 }}>
            {[
              "Tracked trade pricing and status refresh every 30 minutes.",
              "In-app trade notices are automatic. Email and push can be enabled from Account.",
              'Use "Track Trade" to monitor this setup in the Trade Actions panel.',
              'Use "Add to Watchlist" to pin it in your Watchlist panel for quick access.',
            ].map((item) => (
              <Box
                key={item}
                sx={{
                  p: 1.3,
                  borderRadius: 2,
                  border: `1px solid ${isDarkMode ? "rgba(85,105,138,0.4)" : "rgba(176,201,239,0.62)"}`,
                  backgroundColor: isDarkMode
                    ? "rgba(18,27,42,0.62)"
                    : "rgba(247,251,255,0.95)",
                  display: "flex",
                  gap: 1,
                  alignItems: "flex-start",
                }}
              >
                <Box
                  sx={{
                    minWidth: 10,
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #2787FF 0%, #1A61E6 100%)",
                    mt: 0.55,
                    flexShrink: 0,
                  }}
                />
                <Typography
                  sx={{
                    fontSize: "13px",
                    lineHeight: 1.55,
                    color: isDarkMode ? "#E5EEFF" : "#123A67",
                  }}
                >
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>

          <Box
            sx={{
              px: { xs: 2.25, sm: 3 },
              pt: { xs: 0.75, sm: 1 },
              pb: { xs: 2.25, sm: 3 },
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
            }}
          >
            <Typography
              sx={{ fontSize: "12px", color: isDarkMode ? "#86A2CB" : "#567CAE" }}
            >
              Monitoring runs continuously while the trade remains open.
            </Typography>
            <Button
              variant="contained"
              onClick={() => setShowInfoModal(false)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "999px",
                px: 2.5,
                whiteSpace: "nowrap",
                flexShrink: 0,
                background: "linear-gradient(135deg, #2787FF 0%, #1A61E6 100%)",
              }}
            >
              Got it
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      <UpgradeMaxModal
        open={showTrackLimitModal}
        onClose={() => setShowTrackLimitModal(false)}
        onUpgrade={() => {
          setShowTrackLimitModal(false);
          navigate(maxUpgradePathFor(userData));
        }}
        {...trackLimitUpgradeProps}
      />
    </>
  );
};
